/**
 * A team-name field with a Save button (#2836). A lead renames the team;
 * the shared mutation refreshes Home, the header and the admin console.
 */
import { useEffect, useId, useState } from "react";

import { useAdminRenameTeam } from "../api/useAdminRenameTeam";
import { useRenameTeam } from "../api/useTeam";
import { Button } from "./ui/Button";
import { Input, Label } from "./ui/Input";

export interface RenameTeamFieldProps {
  teamId: string;
  currentName: string;
  /** Called with the saved name after a successful rename. */
  onRenamed?: (name: string) => void;
  /**
   * Rename through the admin route (#2877). The admin console sets it: an
   * admin with no seat on the team cannot use the lead route. The lead's own
   * rename leaves it off.
   */
  admin?: boolean;
  /** The demo shows the field and turns it off; `title` says why. */
  disabled?: boolean;
  title?: string | undefined;
}

export function RenameTeamField({
  teamId,
  currentName,
  onRenamed,
  admin = false,
  disabled = false,
  title,
}: RenameTeamFieldProps) {
  const id = useId();
  const leadRename = useRenameTeam(teamId);
  const adminRename = useAdminRenameTeam(teamId);
  const rename = admin ? adminRename : leadRename;
  const [name, setName] = useState(currentName);
  const [saved, setSaved] = useState<string | null>(null);

  // Follow the team's name when it changes elsewhere.
  useEffect(() => {
    setName(currentName);
  }, [currentName]);

  const trimmed = name.trim();
  const unchanged = trimmed === currentName;
  const canSave = !disabled && trimmed.length > 0 && !unchanged && !rename.isPending;

  const save = () => {
    if (!canSave) return;
    setSaved(null);
    rename.mutate(trimmed, {
      onSuccess: (team) => {
        setSaved(team.name);
        onRenamed?.(team.name);
      },
    });
  };

  return (
    <form
      className="flex flex-col gap-1.5"
      data-testid="rename-team"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <Label htmlFor={id}>Team name</Label>
      <div className="flex gap-2">
        <Input
          id={id}
          value={name}
          maxLength={255}
          disabled={disabled}
          title={title}
          onChange={(e) => {
            setName(e.target.value);
            setSaved(null);
            rename.reset();
          }}
          data-testid="rename-team-input"
        />
        <Button
          type="submit"
          size="sm"
          disabled={!canSave}
          title={title}
          data-testid="rename-team-save"
        >
          {rename.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
      {rename.error ? (
        <p className="text-xs text-danger" role="alert" data-testid="rename-team-error">
          Couldn't rename the team: {rename.error.detail}
        </p>
      ) : null}
      {saved ? (
        <output className="text-xs text-fg-muted" data-testid="rename-team-saved">
          Renamed to {saved}.
        </output>
      ) : null}
    </form>
  );
}
